import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { IngestCandidate, IngestConfirmResponse } from '../types';

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// One staged candidate (the input-side language boundary). The model PROPOSED it; the operator
// decides. The value is editable before confirming; rejecting requires a reason. Confirm routes
// through the existing deterministic engine -- the card then reflects that the SYSTEM, not the
// model, evaluated the value.
export default function IngestCandidateCard({ candidate }: { candidate: IngestCandidate }) {
  const [value, setValue] = useState(candidate.observed_value != null ? String(candidate.observed_value) : '');
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<IngestConfirmResponse | null>(null);
  const [decided, setDecided] = useState<null | 'confirmed' | 'rejected'>(null);

  const parsed = value.trim() === '' ? null : Number(value);
  const invalid = parsed !== null && Number.isNaN(parsed);
  const canConfirm = !invalid && (parsed !== null || candidate.observed_value != null);
  const asOf = fmtDate(candidate.as_of);

  const confirm = useMutation({
    mutationFn: () => api.ingestConfirm(candidate.id, parsed),
    onSuccess: (res) => { setOutcome(res); setDecided('confirmed'); },
  });
  const reject = useMutation({
    mutationFn: () => api.ingestReject(candidate.id, reason.trim()),
    onSuccess: () => setDecided('rejected'),
  });

  const accent = decided === 'confirmed' ? 'var(--olive)' : decided === 'rejected' ? 'var(--line)' : 'var(--blue)';

  return (
    <div className="panel" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{candidate.signal_name}</div>
          <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(86,128,194,0.14)', color: 'var(--blue)', border: '1px solid rgba(86,128,194,0.4)' }}>
            {candidate.provenance}
          </span>
        </div>
        {candidate.low_confidence ? (
          <span className="tag amber" style={{ fontSize: 11 }}>Low confidence</span>
        ) : (
          <span className="tag olive" style={{ fontSize: 11 }}>Confidence {Math.round(candidate.confidence * 100)} percent</span>
        )}
      </div>

      {/* the exact source span the value traces to */}
      <div style={{ marginTop: 10, borderLeft: '2px solid var(--line)', paddingLeft: 10, fontSize: 14, color: 'var(--muted)', fontStyle: 'italic' }}>
        &ldquo;{candidate.source_quote}&rdquo;
      </div>

      <div className="small faint" style={{ marginTop: 8, fontSize: 12 }}>
        Proposed reading:{' '}
        <b style={{ color: 'var(--ink)' }}>{candidate.observed_value}{candidate.unit ? ` ${candidate.unit}` : ''}</b>
        {asOf ? ` · as of ${asOf}` : ''}
      </div>

      {decided === null && (
        <>
          <div className="row" style={{ gap: 10, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="small faint" style={{ fontSize: 12 }} htmlFor={`val-${candidate.id}`}>Confirm value</label>
            <input id={`val-${candidate.id}`} className="c2-input" style={{ width: 110 }} value={value} onChange={(e) => setValue(e.target.value)} placeholder="value" aria-label="Confirm value (editable before confirming)" />
            {candidate.unit && <span className="small faint" style={{ fontSize: 12 }}>{candidate.unit}</span>}
            <button type="button" className="btn primary" onClick={() => confirm.mutate()} disabled={confirm.isPending || !canConfirm}>
              {confirm.isPending ? 'Recording…' : 'Confirm'}
            </button>
            {!canConfirm && <span className="small faint" style={{ fontSize: 12 }}>Enter a number to confirm.</span>}
          </div>
          <div className="row" style={{ gap: 10, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="c2-input" style={{ flex: 1, minWidth: 180 }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejecting (required)" aria-label="Reason for rejecting" />
            <button type="button" className="btn ghost" onClick={() => reject.mutate()} disabled={reject.isPending || reason.trim() === ''}>
              Reject
            </button>
          </div>
        </>
      )}

      {decided === 'confirmed' && outcome && (
        <div className="panel" style={{ marginTop: 12, background: 'var(--bg)' }}>
          <div className="small">Recorded to <b>{candidate.signal_name}</b>; the system evaluated it.</div>
          <div className="small" style={{ marginTop: 4 }}>
            {outcome.breached ? (
              <>
                The system flagged a breach.{' '}
                {outcome.drift_packet_id && <Link to={`/drift/${outcome.drift_packet_id}`}>Open the drift packet</Link>}
              </>
            ) : (
              'The system recorded the value within tolerance.'
            )}
          </div>
        </div>
      )}

      {decided === 'rejected' && (
        <div className="small faint" style={{ marginTop: 12 }}>Rejected. Nothing reached the engines.</div>
      )}
    </div>
  );
}
